import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { AuthService, BreadcrumbService, HomeService, ProductNameService, StateService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { 
  ListRenderedOutputsRequest, ListRenderedOutputsResponse, ProductName, ProductType, RenderedOutput, RenderedOutputHome, RenderedOutputStatus, RenderedOutputSummary, SituViewWebApiClient
} from 'situview-webapi-client';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'home-config-list',
  templateUrl: './home-config-list.component.html',
  styleUrls: ['./home-config-list.component.less']
})
export class HomeConfigListComponent extends SituBaseComponent implements OnChanges {
  @Input() product: ProductName | undefined;
  @Input() refreshToken: string | undefined;
  @Input() viewRenderedOutput: RenderedOutputSummary | undefined;
  @Input() compareRenderedOutput: RenderedOutputSummary | undefined;

  @Output() selectedRenderedOutputId: EventEmitter<string> = new EventEmitter();
  @Output() selectedComparisonRenderedOutputId: EventEmitter<string> = new EventEmitter<string>();
  @Output() onIssueSelectClick: EventEmitter<string> = new EventEmitter<string>();

  mostPopular: boolean = false;
  filter: HomeConfigFilter | undefined;
  listType: RenderedOutputStatus = RenderedOutputStatus.Complete;
  productTypeFilter: ProductName | undefined;
  productTypes = ProductName;
  userFilter: string | undefined;
  bugTracking: boolean = false;
  scratchOnly: boolean = false;
  /** Only this home's renders; undefined for all. */
  homeFilter: string | undefined;
  /** The homes with renders matching the other filters, read when the dropdown opens. */
  homes: RenderedOutputHome[] = [];
  homesLoading = false;
  /** True when the company or another filter has changed since the homes were read. */
  private homesStale = true;
  /** The chosen home, kept as an option while the homes list doesn't carry it. */
  private chosenHome: RenderedOutputHome | undefined;
  private destroyed = false;

  renderedOutputStatus = RenderedOutputStatus;

  completeConfigs: RenderedOutputSummary[] = [];
  inProgressConfigs: RenderedOutputSummary[] = [];
  queuedConfigs: RenderedOutputSummary[] = [];
  failedConfigs: RenderedOutputSummary[] = [];
  filteredHistoryConfigs: RenderedOutputSummary[] = [];

  renderedOutputId: string | undefined;
  comparisonRenderedOutputId: string | undefined;

  private _loading = false;
  get loading(): boolean { return this._loading; }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private homeService: HomeService,
    private productNameService: ProductNameService) {
      super(authService, stateService)
    }

  protected onSelectedCompanyChange(): void {
    this.homes = [];
    this.homesStale = true;
    this.chosenHome = undefined;
    this.homeFilter = undefined;
    this.loadRenderedOutputs();
  }

  /** '' is All: mat-select has no value for undefined. */
  setHomeFilter(homeId: string): void {
    const id = homeId || undefined;
    if (id === this.homeFilter) { return; }
    this.homeFilter = id;
    this.chosenHome = id ? this.homes.find(h => h.homeId === id) : undefined;
    this.loadRenderedOutputs();
  }

  /** The homes to offer, plus the chosen one when it has dropped out of the list (e.g. after a filter change). */
  get homeOptions(): RenderedOutputHome[] {
    const chosen = this.chosenHome;
    return chosen && !this.homes.some(h => h.homeId === chosen.homeId) ? [chosen, ...this.homes] : this.homes;
  }

  /**
   * The homes are read when the dropdown opens and only if a filter changed since the last read.
   * SituView takes the list request itself, minus the home, and answers with the homes it covers.
   */
  onHomesOpened(opened: boolean): void {
    if (!opened || !this.homesStale || this.homesLoading) { return; }
    const request = this.buildRequest(RenderedOutputStatus.Complete);
    request.homeId = undefined;
    const key = JSON.stringify(request);
    this.homesLoading = true;
    this.homeService.listRenderedOutputHomes(request)
      .then(homes => {
        const current = this.buildRequest(RenderedOutputStatus.Complete);
        current.homeId = undefined;
        if (this.destroyed || JSON.stringify(current) !== key) { return; }
        this.homes = homes;
        this.homesStale = false;
      })
      .catch(() => { /* the dropdown keeps what it had; the next open tries again */ })
      .finally(() => { this.homesLoading = false; });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.productTypeFilter = this.product === ProductName.Facade ? ProductName.Facade : ProductName.Panorama;
    this._subscriptions.push(this.homeService.cacheInvalidation$.subscribe(() => {
      // a render finished: the home counts have moved too, so the dropdown re-reads when next opened
      this.homesStale = true;
      this.loadRenderedOutputs();
    }));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    super.ngOnDestroy();
  };

  ngOnChanges(changes: SimpleChanges): void {
    // if (changes.refreshToken && !changes.refreshToken.isFirstChange() && changes.refreshToken.currentValue) {
    //   this.loadRenderedOutputs();
    // }
  }

  select(renderedOutputId: string) {
    this.selectedRenderedOutputId.emit(renderedOutputId);
  }

  selectForComparison(renderedOutputId: string) {
    this.selectedComparisonRenderedOutputId.emit(renderedOutputId)
  }

  onIssueSelect(issueUrl: string) {
    this.onIssueSelectClick.emit(issueUrl);
  }

  setProductFilter(productName: ProductName) {
    this.bugTracking = false;
    // The render-type icons and Scratch are one switch: picking a type switches away from scratch.
    this.scratchOnly = false;
    this.productTypeFilter = productName;
    this.homesStale = true;
    this.loadRenderedOutputs();
  }

  toggleUserFilter() {
    if (this.userFilter) {
      this.userFilter = undefined;
    } else {
      this.userFilter = this.authService.getUserId();
    }
    this.homesStale = true;
    this.loadRenderedOutputs();
  }

  toggleIssueFilter() {
    this.homesStale = true;
    this.bugTracking = !this.bugTracking;
    if (this.bugTracking) {
      this.userFilter = undefined;
      this.scratchOnly = false;
      this.productTypeFilter = this.product === ProductName.Facade ? ProductName.Facade : ProductName.Panorama;
    }
    this.loadRenderedOutputs();
  }

  toggleScratchFilter() {
    this.homesStale = true;
    this.scratchOnly = !this.scratchOnly;
    if (this.scratchOnly) {
      this.bugTracking = false;
      // Scratch is its own switch position — clear the render-type selection so it reads as
      // "showing scratches", not a scratch filter stacked on top of a product type.
      this.productTypeFilter = undefined;
    } else {
      this.productTypeFilter = this.product === ProductName.Facade ? ProductName.Facade : ProductName.Panorama;
    }
    this.loadRenderedOutputs();
  }

  loadHomeConfig(renderedOutputSummary: RenderedOutputSummary) {
    this.renderedOutputId = renderedOutputSummary.id;
    this.selectedRenderedOutputId.emit(renderedOutputSummary.id);
  }

  getProductColour(productName?: ProductName) {
    return productName !== undefined ? 
    this.productNameService.getProductNameConfiguration(productName)?.productColor :
      this.productNameService.getProductNameConfiguration(this.product)?.productColor;
  }

  /** The list request for the current filters; the home dropdown sends the same one, minus the home. */
  private buildRequest(status: RenderedOutputStatus): ListRenderedOutputsRequest {
    const request = new ListRenderedOutputsRequest();
    request.companyId = this.selectedCompany?.id;
    request.pageNumber = 0;
    request.pageSize = 10;
    if (this.scratchOnly) {
      // Scratch shows all of the page's scratch renders, not scoped to one render sub-type.
      request.productName = this.product === ProductName.Facade ? ProductName.Facade : ProductName.Panorama;
      request.expandProducts = true;
    } else {
      request.productName = this.product === ProductName.Facade ? ProductName.Facade : this.productTypeFilter ?? ProductName.Panorama;
    }
    request.status = status;
    request.scratchOnly = this.scratchOnly;
    if (this.userFilter) request.createdBy = this.userFilter;
    if (this.homeFilter) request.homeId = this.homeFilter;
    return request;
  }

  loadRenderedOutputs(status: RenderedOutputStatus = RenderedOutputStatus.Complete) {
    this.loading = true;
    const request = this.buildRequest(status);

    this.homeService
      .listRenderedOutputs(request)
      .then((renderedOutputs: RenderedOutput[]) => {
        const copiedData = JSON.parse(JSON.stringify(renderedOutputs));

        switch (status) {
          case RenderedOutputStatus.Complete:
            this.listType = RenderedOutputStatus.Complete;
            this.completeConfigs = copiedData;
            break;
          case RenderedOutputStatus.Cancelled:
          case RenderedOutputStatus.Failed:
            this.listType = RenderedOutputStatus.Failed;
            this.failedConfigs = copiedData;
            break;
          case RenderedOutputStatus.InProgress:
            this.listType = RenderedOutputStatus.InProgress;
            this.inProgressConfigs = copiedData;
            break;
          case RenderedOutputStatus.Queued:
            this.listType = RenderedOutputStatus.Queued;
            this.queuedConfigs = copiedData;
            break;
        }
      })
      .catch(() => {
        this.showMessage(
          "Sorry! There was an issue at our end retrieving your renders. Please try again."
        );
      })
      .finally(() => {
        this.loading = false;
      });
  }


  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  getProductName() {
    switch (this.product) {
      case ProductName.Panorama:
      case ProductName.RayTracedDraft:
      case ProductName.RayTraced:
      case ProductName.RayTracedPlus:
        return "Panorama";
      case ProductName.Facade:
      case ProductName.Insights:
      case ProductName.Plan:
      case ProductName.VR:
        return ProductName[this.product];
    }
  }
}

export class HomeConfigFilter {
  constructor(public type: string, public value: string, public listType: string) {
  }
}